
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

/* =====================================================
   CREATE SERVICEMEN PROFILE
===================================================== */

export const createServicemen = createAsyncThunk(
  "servicemen/createServicemen",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/servicemen/create-sprofile", formData);
      if (!data.success) return rejectWithValue(data.message);
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to create profile"
      );
    }
  }
);

/* =====================================================
   GET MY PROFILE
===================================================== */
export const getMyServicemenProfile = createAsyncThunk(
  "servicemen/getMyProfile",
  async (userId, { rejectWithValue }) => {
    try {
      console.log("THUNK CALLED WITH ID:", userId);
      const { data } = await api.get(`/single-sprofile/${userId}`);

      console.log("thunk called api", data.data);
      
      if (!data.success) return rejectWithValue(data.message);
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch profile"
      );
    }
  }
);

/* =====================================================
   UPDATE PROFILE
===================================================== */
export const updateServicemen = createAsyncThunk(
  "servicemen/updateServicemen",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/update-servicemen/${id}`, formData);
      if (!data.success) return rejectWithValue(data.message);
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update profile"
      );
    }
  }
);

/* =====================================================
   ADMIN: GET ALL SERVICEMEN
===================================================== */
export const getAllServicemen = createAsyncThunk(
  "servicemen/getAll",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/all-servicemen?page=${page}&limit=${limit}`
      );
      if (!data.success) return rejectWithValue(data.message);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch servicemen"
      );
    }
  }
);

/* =====================================================
   ✅ NEW: GET SERVICEMEN BY CATEGORY + CITY
===================================================== */
export const getServicemenByCategory = createAsyncThunk(
  "servicemen/getByCategory",
  async (
    { categoryId, page = 1, limit = 10, city },
    { rejectWithValue }
  ) => {
    try {
      
      let url = `/category/servicmen/${categoryId}?page=${page}&limit=${limit}`;

      if (city) {
        url += `&city=${city}`;
      }

      const { data } = await api.get(url);

      if (!data.success) return rejectWithValue(data.message);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
        "Failed to fetch servicemen by category"
      );
    }
  }
);

/* =====================================================
   ADMIN: TOGGLE VERIFICATION
===================================================== */
export const toggleVerification = createAsyncThunk(
  "servicemen/toggleVerification",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/servicemen/verify/${id}`);
      if (!data.success) return rejectWithValue(data.message);
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update verification"
      );
    }
  }
);

/* =====================================================
   ADMIN: DELETE SERVICEMEN
===================================================== */
export const deleteServicemen = createAsyncThunk(
  "servicemen/deleteServicemen",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/delete-servicemen/${id}`);
      if (!data.success) return rejectWithValue(data.message);
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete profile"
      );
    }
  }
);

/* =====================================================
   SLICE
===================================================== */
const servicemenSlice = createSlice({
  name: "servicemen",
  initialState: {
    loading: false,
    myProfile: null,
    allServicemen: [],
    pagination: null,
    successMessage: null,
    errorMessage: null,
  },

  reducers: {
    clearServicemenMessages: (state) => {
      state.successMessage = null;
      state.errorMessage = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // CREATE PROFILE
      .addCase(createServicemen.pending, (state) => {
        state.loading = true;
      })
      .addCase(createServicemen.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Profile created successfully!";
        state.myProfile = action.payload;
      })
      .addCase(createServicemen.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })

      // MY PROFILE
      .addCase(getMyServicemenProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyServicemenProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.myProfile = action.payload;
      })
      .addCase(getMyServicemenProfile.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })

      // UPDATE
      .addCase(updateServicemen.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateServicemen.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Profile updated successfully!";
        state.myProfile = action.payload;
      })
      .addCase(updateServicemen.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })

      // GET ALL
      .addCase(getAllServicemen.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllServicemen.fulfilled, (state, action) => {
        state.loading = false;
        state.allServicemen = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllServicemen.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })

      // ✅ GET BY CATEGORY
      .addCase(getServicemenByCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServicemenByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.allServicemen = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getServicemenByCategory.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })

      // TOGGLE VERIFIED
      .addCase(toggleVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Verification updated!";
        state.allServicemen = state.allServicemen.map((item) =>
          item._id === action.payload._id ? action.payload : item
        );
      })

      // DELETE
      .addCase(deleteServicemen.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Servicemen profile deleted!";
        state.allServicemen = state.allServicemen.filter(
          (item) => item._id !== action.payload
        );
      });
  },
});

export const { clearServicemenMessages } = servicemenSlice.actions;
export default servicemenSlice.reducer;
